// CATEGORY_COLOR_PRESETS - Curated color palette for POI categories
export const CATEGORY_COLOR_PRESETS = [
  // Greens
  "#22C55E", // green-500
  "#16A34A", // green-600
  "#15803D", // green-700
  "#059669", // emerald-600
  "#0D9488", // teal-600

  // Blues
  "#3B82F6", // blue-500
  "#2563EB", // blue-600
  "#1D4ED8", // blue-700
  "#0EA5E9", // sky-500
  "#06B6D4", // cyan-500

  // Purples & Pinks
  "#8B5CF6", // violet-500
  "#7C3AED", // violet-600
  "#A855F7", // purple-500
  "#D946EF", // fuchsia-500
  "#EC4899", // pink-500

  // Oranges & Reds
  "#F97316", // orange-500
  "#EA580C", // orange-600
  "#EF4444", // red-500
  "#DC2626", // red-600
  "#F59E0B", // amber-500

  // Yellows & Neutrals
  "#EAB308", // yellow-500
  "#84CC16", // lime-500
  "#6B7280", // gray-500
  "#64748B", // slate-500
  "#78716C", // stone-500
] as const;

export type CategoryColor = (typeof CATEGORY_COLOR_PRESETS)[number];

// CURATED_FA_ICONS - FontAwesome Pro Solid icons organized by category for POI selection
export const CURATED_FA_ICONS = {
  animals: [
    "paw",
    "hippo",
    "otter",
    "fish",
    "fish-fins",
    "dove",
    "crow",
    "kiwi-bird",
    "dragon",
    "spider",
    "bug",
    "frog",
    "cat",
    "dog",
    "horse",
    "feather",
    "whale",
    "turtle",
    "elephant",
    "deer",
    "duck",
    "butterfly",
    "bee",
    "squirrel",
    "rabbit",
    "snake",
    "crab",
    "shrimp",
    "worm",
    "mosquito",
  ],
  nature: [
    "tree",
    "tree-palm",
    "leaf",
    "leaf-oak",
    "leaf-maple",
    "flower",
    "flower-tulip",
    "seedling",
    "mushroom",
    "cactus",
    "mountain",
    "mountain-sun",
    "water",
    "sun",
    "cloud",
    "cloud-sun",
    "cloud-rain",
    "snowflake",
    "wind",
    "fire",
    "volcano",
    "rainbow",
  ],
  food: [
    "utensils",
    "burger",
    "pizza-slice",
    "ice-cream",
    "coffee",
    "mug-hot",
    "mug-saucer",
    "cup-straw",
    "wine-glass",
    "beer-mug-empty",
    "martini-glass",
    "candy-cane",
    "cookie",
    "cake-candles",
    "apple-whole",
    "carrot",
    "hotdog",
    "taco",
    "bowl-chopsticks-noodles",
    "plate-wheat",
    "egg",
    "bacon",
    "cheese",
    "pepper-hot",
  ],
  shopping: [
    "store",
    "shop",
    "bag-shopping",
    "cart-shopping",
    "basket-shopping",
    "gift",
    "shirt",
    "hat-cowboy",
    "gem",
    "tag",
    "receipt",
    "barcode",
    "shoe-prints",
    "ring",
    "scissors",
    "diamond",
  ],
  attractions: [
    "star",
    "wand-magic-sparkles",
    "masks-theater",
    "castle",
    "fort",
    "monument",
    "archway",
    "bridge",
    "bridge-suspension",
    "landmark",
    "tower-observation",
    "place-of-worship",
    "gopuram",
    "torii-gate",
    "church",
    "mosque",
    "synagogue",
    "vihara",
    "kaaba",
  ],
  entertainment: [
    "ferris-wheel",
    "ticket",
    "film",
    "gamepad",
    "gamepad-modern",
    "dice",
    "chess",
    "puzzle-piece",
    "music",
    "guitar",
    "drum",
    "microphone",
    "microphone-stand",
    "headphones",
    "compact-disc",
    "radio",
    "tv",
    "video",
    "popcorn",
    "clapperboard",
    "bowling-ball",
    "table-tennis-paddle-ball",
  ],
  services: [
    "circle-info",
    "bell-concierge",
    "circle-question",
    "phone",
    "envelope",
    "envelope-open",
    "wifi",
    "signal",
    "charging-station",
    "bolt",
    "plug",
    "gear",
    "wrench",
    "screwdriver-wrench",
    "toolbox",
    "key",
    "lock",
    "lock-open",
    "shield",
    "shield-check",
  ],
  facilities: [
    "restroom",
    "toilet",
    "toilet-portable",
    "home",
    "baby",
    "baby-carriage",
    "person-pregnant",
    "wheelchair",
    "hands-holding-child",
    "elevator",
    "escalator",
    "stairs",
    "door-open",
    "shower",
    "faucet",
    "sink",
    "hand-holding-droplet",
    "bench-tree",
    "lamp-street",
  ],
  transportation: [
    "car",
    "car-side",
    "bus",
    "bus-school",
    "train",
    "train-tram",
    "subway",
    "taxi",
    "bicycle",
    "motorcycle",
    "scooter",
    "cable-car",
    "ship",
    "ferry",
    "sailboat",
    "plane",
    "helicopter",
    "jet-fighter",
    "rocket",
    "parking",
    "gas-pump",
  ],
  activities: [
    "dumbbell",
    "person-walking",
    "person-running",
    "person-swimming",
    "person-hiking",
    "person-biking",
    "person-skiing",
    "person-snowboarding",
    "person-skating",
    "campground",
    "tent",
    "umbrella-beach",
    "fishing-rod",
    "horse-saddle",
    "medal",
    "award",
  ],
  safety: [
    "kit-medical",
    "suitcase-medical",
    "fire-extinguisher",
    "life-ring",
    "triangle-exclamation",
    "shield",
    "shield-check",
  ],
  finance: [
    "dollar-sign",
    "credit-card",
    "money-bill",
    "coins",
    "wallet",
    "piggy-bank",
    "building-columns",
    "landmark-dome",
    "vault",
    "cash-register",
  ],
  amenities: [
    "bench-tree",
    "couch",
    "chair",
    "bed",
    "lamp",
    "lamp-street",
    "lightbulb",
    "fan",
    "trash-can",
    "recycle",
    "dumpster",
    "box",
    "droplet",
    "glass-water",
  ],
  general: [
    "palette",
    "calendar",
    "location-dot",
    "map-pin",
    "flag",
    "bookmark",
    "circle",
    "square",
    "heart",
    "thumbs-up",
    "eye",
    "magnifying-glass",
    "check",
    "xmark",
    "plus",
    "minus",
    "arrows-rotate",
  ],
  medical: [
    "stethoscope",
    "pills",
    "hospital",
    "syringe",
    "heart-pulse",
    "bandage",
    "dna",
    "x-ray",
    "briefcase-medical",
    "head-side-mask",
    "ambulance",
    "lungs",
    "virus-slash",
    "microscope",
  ],
  education: [
    "graduation-cap",
    "book-open-reader",
    "school",
    "university",
    "chalkboard",
    "chalkboard-teacher",
    "laptop-code",
    "atom",
    "pencil-alt",
  ],
  sports: [
    "baseball",
    "baseball-bat-ball",
    "basketball",
    "football",
    "futbol",
    "golf-ball-tee",
    "volleyball",
    "tennis-ball",
    "hockey-puck",
    "bowling-ball",
    "table-tennis-paddle-ball",
    "swimming-pool",
    "dumbbell",
  ],
  buildings: [
    "hotel",
    "warehouse",
    "cabin",
    "building",
    "house-chimney",
    "school",
    "university",
    "hospital",
    "church",
    "mosque",
  ],
} as const;

export type IconCategory = keyof typeof CURATED_FA_ICONS;
export type CuratedIcon = (typeof CURATED_FA_ICONS)[IconCategory][number];

export const ALL_CURATED_ICONS = Object.values(CURATED_FA_ICONS).flat();
